import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsDeliveryQueryKey,
  useGetSettingsDelivery,
  usePutSettingsDelivery,
} from '@agos/api-client/platform'
import {
  chosenMapApp,
  MAP_APPS,
  MAP_APP_LABELS,
  mapAppCaveat,
  T,
  tt,
  type MapApp,
} from '@agos/shared'
import { ReadFailedBlock, Spinner, toast, BUTTON_PRIMARY, cn} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, MapPin, Navigation, Save } from 'lucide-react'
import { useEffect, useState } from 'react'

import { AddressField } from '@agos/ui'

/**
 * The depot address - one field, and it is load-bearing.
 *
 * The depot is where drivers start and return, so it is the origin every
 * delivery's kilometres are measured from - which means it is also the origin of
 * every driver's per-kilometre pay. A wrong depot does not fail; it quietly
 * mis-pays everybody.
 *
 * **A section rather than a page since §9.302.** `/delivery-settings` is now a hub and this is its
 * first tab, so the `PageHeader` moved up to `DeliveryHubPage` - two of them stacked would have read
 * *Nastavenia rozvozu* twice. The card's own heading, *Východiskový bod (depot)*, still names it.
 *
 * Ported from legacy `pages/delivery-settings.tsx`.
 */

const SAVED_FLASH_MS = 2000

export function DepotSection() {
  const queryClient = useQueryClient()
  const saveDelivery = usePutSettingsDelivery()
  const { data, isLoading, isError, refetch } = useGetSettingsDelivery()

  const [depotAddress, setDepotAddress] = useState('')
  // §9.509, owner O29 - which map the driving surface opens. `chosenMapApp` keeps an unrecognised stored value
  // from putting the select into a state it cannot show.
  const [mapApp, setMapApp] = useState<MapApp>('google')
  const [loadedFor, setLoadedFor] = useState<unknown>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  // Adjust state when the input changed, rather than syncing from an effect:
  // the field is the user's while they are typing, and the server's before that.
  if (data !== loadedFor) {
    setLoadedFor(data)
    setDepotAddress((data as { depotAddress?: string } | undefined)?.depotAddress ?? '')
    setMapApp(chosenMapApp((data as { mapApp?: string } | undefined)?.mapApp))
  }

  useEffect(() => {
    if (!justSaved) return
    const timer = setTimeout(() => setJustSaved(false), SAVED_FLASH_MS)
    return () => clearTimeout(timer)
  }, [justSaved])

  const save = async () => {
    setIsSaving(true)
    setJustSaved(false)
    try {
      // The generated hook, now that the spec describes this PUT. It used to be a hand-rolled
      // `customFetch` with a body nothing checked.
      /* §9.509 - **a named variable rather than a literal, and that is the whole trick.** `DeliverySettingsRequest`
       * in the generated client carries `depotAddress` alone: the spec snapshot predates §10A.260, and regenerating
       * it needs the spec re-exported from a running backend. TypeScript's excess-property check fires only on a
       * fresh object literal, so a typed variable reaches `mutateAsync` with no cast and no generated file touched -
       * which also keeps `ci.sh --strict`'s freshness gate happy, since nothing under `src/generated` moves.
       *
       * The server has accepted `mapApp` since §10A.260 and validates it to the same four, so the extra field is
       * true of the endpoint rather than hopeful. */
      const patch = { depotAddress: depotAddress.trim(), mapApp }
      await saveDelivery.mutateAsync({ data: patch })
      await queryClient.invalidateQueries({ queryKey: getGetSettingsDeliveryQueryKey() })
      setJustSaved(true)
      toast({ title: tt('Uložené'), description: tt('Nastavenia rozvozu boli uložené') })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uložiť nastavenia')),
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    )
  }

  /* §9.622, FB-930 - **the editor is declined rather than the save disabled, and the save's own guard was
   * luck.** A failed read leaves `depotAddress` at `''` and `mapApp` at `chosenMapApp(undefined)`, which is
   * `google`; measured on both franchises' rows, `depotAddress` is **set** and `mapApp` is **absent**, so the
   * blank address is a false claim today and the map select is right by coincidence. The save is
   * `disabled={... || !depotAddress.trim()}`, written to stop an empty depot being stored, which happens to
   * stop this form being saved at all - FB-818's and FB-821's shape, and §9.543's rule says convert it rather
   * than document it. The boxes are what somebody reads (§9.531), and this is the address every delivery's
   * kilometres - and every driver's per-kilometre pay - are measured from. */
  if (isError) {
    return (
      <div className="space-y-6">
        <ReadFailedBlock
          marker="read"
          of="delivery-settings"
          title={tt('Nastavenia rozvozu sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>
            Preto tu nie je ani formulár - prázdne pole by vyzeralo ako nenastavený depot a v mape by bol
            vybraný Google, aj keby ste mali zvolenú inú aplikáciu.
          </T>
        </ReadFailedBlock>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card">
        <div className="px-5 py-4 border-b border-border bg-muted/30 rounded-t-xl">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <T>Východiskový bod (depot)</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <T>
              Adresa, odkiaľ rozvozári vyštartujú a kam sa vracajú. Používa sa na výpočet celkových
              km.
            </T>
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <AddressField
              label={tt('Adresa depota')}
              value={depotAddress}
              onChange={setDepotAddress}
              placeholder={tt('Napr. M.R. Štefánika 191/3 Šurany')}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              <T>
                Zadajte presnú adresu vrátane ulice, čísla a mesta. Adresa sa použije pre výpočet
                vzdialeností.
              </T>
            </p>
          </div>

          <div>
            <label
              htmlFor="delivery-map-app"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              <Navigation className="w-3.5 h-3.5 inline mr-1.5 text-primary" />
              <T>Predvolená mapa pre Rozvoz aplikáciu</T>
            </label>
            <select
              id="delivery-map-app"
              data-map-app
              value={mapApp}
              onChange={(event) => setMapApp(chosenMapApp(event.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
            >
              {MAP_APPS.map((app) => (
                <option key={app} value={app}>
                  {MAP_APP_LABELS[app]}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1.5">
              <T>
                Ktorá aplikácia sa otvorí, keď rozvozár klikne na Navigovať. Zamestnanec si môže
                nastaviť vlastnú.
              </T>
            </p>
            {mapAppCaveat(mapApp) !== null && (
              <p data-map-app-caveat className="text-xs text-amber-500 mt-1.5 font-medium">
                <T>{mapAppCaveat(mapApp)}</T>
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={isSaving || !depotAddress.trim()}
              className={cn('inline-flex items-center gap-2 px-5 py-2.5 rounded-lg', BUTTON_PRIMARY, 'text-sm disabled:cursor-not-allowed')}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : justSaved ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSaving ? tt('Ukladám...') : justSaved ? tt('Uložené') : tt('Uložiť')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
